/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { PvhAuditQuantityAndReasonCodes, PvhLineItemOverride, PvhReasonCodeAndComment, PvhReasonCodeOption, PvhScanChamberAttemptLineDto, PvhScanChamberService, PvhScanChamberStateEnum, PvhScanChamberStateInfoDto } from "app/services/pvh.scan-chamber.service";
import { PvhScanChamberReasonCodeAndCommentsDialog } from "../reason-code-and-comments-dialog/pvh.scan-chamber.reason-code-and-comments.dialog.component";
import { PvhScanChamberOverrideAuditQuantityDialog } from "../audit-quantity-dialog/pvh.scan-chamber.audit-quantity.dialog.component";

/** Number of milliseconds between page refresh api calls */
const RefreshTimeout = 1000;

@Component({
  templateUrl: './pvh.scan-chamber.dashboard.component.html',
  styleUrls: ['./pvh.scan-chamber.dashboard.component.css']
})
export class PvhScanChamberDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) 
  indefiniteProgressDialog!: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild(SimpleSuccessErrorDialogComponent) 
  simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  /** Reference to the Dialog used to allow the User to select a Reason Code and optionally enter a comment */
  @ViewChild(PvhScanChamberReasonCodeAndCommentsDialog) 
  reasonCodeAndCommentDialog!: PvhScanChamberReasonCodeAndCommentsDialog;

  /** Reference to the Dialog used to allow the User to manually set the Audit Quantity and apply Reason Codes / Comments to an individual Line Item */
  @ViewChild(PvhScanChamberOverrideAuditQuantityDialog) 
  auditQuantityDialog!: PvhScanChamberOverrideAuditQuantityDialog;

  /** Carton ID Input Field HTML Element Reference */
  @ViewChild('cartonIdField') cartonIdField!: ElementRef;

  /** Service whose Dashboard is being displayed */
  service?: ServiceInstance;

  /** Current State of the Scan Chamber being reflected in the Dashboard */
  currentState?: PvhScanChamberStateInfoDto;

  /** Carton ID value entered by the User in the UI */
  cartonId?: string;

  /** Available Reason Code Options for the User */
  availableReasonCodeOptions: PvhReasonCodeOption[] = [];

  /** Holds the User entered Reason Code / Comment tied to the attempt */
  enteredReasonCodeAndComment?: PvhReasonCodeAndComment;

  auditQuantityMap: Map<string, number> = new Map<string, number>();
  lineItemReasonCodeAndCommentMap: Map<string, PvhReasonCodeAndComment[]> = new Map<string, PvhReasonCodeAndComment[]>();

  /** Flag used to indicate whether a User Action is being processed or not.  Used to disable some UI elements and prevent multiple actions from occuring simulatenously. */
  isProcessingAction = false;

  /** Flag used to indicate whether this page was navigated away from.  Used to stop the refresh task. */
  isDestroyed = false;


  /** Constructor */
  constructor(
    private pvhService: PvhScanChamberService,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    console.log(`ngOnInit()`);

    // Grab the data from the resolvers
    this.route.data.subscribe(data => {
      const selectedService = (data['selectedService'] as ServiceInstance);
      this.service = selectedService;

      const state = (data['state'] as PvhScanChamberStateInfoDto);
      this.setState(state);

      const reasonCodes = (data['reasonCodes'] as PvhReasonCodeOption[]);
      this.availableReasonCodeOptions.push(...reasonCodes);

      // Start the "Refresh" Task in the background
      setTimeout(this.refreshData.bind(this), RefreshTimeout);
    });
  }

  /**
   * Destroys the component.
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
   * Returns a boolean indicating if the current State is "Enter Info",
   * meaning it is waiting on the User to scan the Carton.
   */
  isDisplayState_EnterInfo(): boolean {
    return this.currentState?.state == PvhScanChamberStateEnum.WaitingOnCartonScan;
  }

  /**
   * Returns a string representation for the current State.
   */
  getDisplayState(displayState?: PvhScanChamberStateEnum): string {
    if (displayState) {
      switch(displayState){
        case PvhScanChamberStateEnum.WaitingOnCartonScan :
          return "Waiting On Barcode Scan...";
        case PvhScanChamberStateEnum.RetrievingCartonContents :
          return "Retrieving Carton Contents...";
        case PvhScanChamberStateEnum.CartonBeingValidated :
          return "Validating Carton...";
        case PvhScanChamberStateEnum.CartonBeingProcessed :
          return "Processing Carton...";
        default:
          return "Unknown State!!!";
      }
    } 
    return "Undefined State!!!";
  }
  
  /**
   * Task which is continously executing in the background while the component
   * is displayed.  Queries for the current state of the Scan Chamber and updates
   * the displayed values. 
   * 
   * This also sets the focus to the "Carton ID" Input Field so that it
   * keeps focus so that the USB Barcode Scanner works as expected.
   */
  private async refreshData(): Promise<void> {
    if (!this.isDestroyed && this.service) {
      try {
        // Query for / Update the State
        const state = await this.pvhService.getState(this.service.id);
        this.setState(state);
      } finally {
        // Focus on the "Carton ID" Input Field if available
        if (this.cartonIdField?.nativeElement != null) {
          console.log("Focusing Carton ID");
          this.cartonIdField.nativeElement.focus();
        }

        // Reschedule the "Refresh" Task if the component hasn't been destroyed.
        if (!this.isDestroyed) {
          setTimeout(this.refreshData.bind(this), RefreshTimeout);
        }
      }
    }
  }

  /**
   * Sets the current state being displayed to the User
   */
  private setState(state: PvhScanChamberStateInfoDto): void {
    // Sort the attempt by the amount still required to be scanned
    if (state.attempt && state.attempt.line_items.length > 0) {
      state.attempt.line_items.sort((a, b) => (b.required_quantity - b.scanned_quantity) - (a.required_quantity - a.scanned_quantity));
    }

    // Only reflect a state change if it actually changes
    if (JSON.stringify(this.currentState) != JSON.stringify(state)) {
      console.log(`Updating State: ${JSON.stringify(state)}`);
      this.currentState = state;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Called when the "Enter" Key Up Event is called on the "Carton ID" Input Field.
   * This calls the "Enter Info" Function on the Scan Chamber in the background.
   */
  enterCartonId(): void {
    console.log(`enterCartonId()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this._enterCartonId();
    }
  }

  /**
   * Background Task for handling calling the "Enter Info" Function on the Scan Chamber.
   * Calls the Function to load the Carton Information to be validated, then
   * updates the screen. 
   */
  private async _enterCartonId(): Promise<void> {
    console.log(`enterCartonId() - Carton ID: '${this.cartonId}'`);
    if (this.cartonId && this.cartonId.length > 0 && this.service) {
      // Call the "Enter Info" API
      let state: PvhScanChamberStateInfoDto;
      try {
        state = await this.pvhService.enterCartonId(this.service.id, this.cartonId);
      } catch (error) {
        this.handleError(error);
        this.cartonId = "";
        this.isProcessingAction = false;
        this.changeDetectorRef.detectChanges();
        return;
      }

      // Update the state if one was retrieved, otherwise let the "Refresh" Task take care of it
      if (state != null) {
        this.setState(state);
      }

      // Update the UI
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    } else {
      // TODO: Display error stating that the Carton ID is missing
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    }
  }

  /**
   * Called when the "Clear Carton" UI Button is clicked.
   * Calls the "Clear Carton" Function on the Scan Chamber in the background.
   */
  onClearCartonClicked(): void {
    console.log(`onClearCartonClicked()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this._clearCarton();
    }
  }

  /**
   * Background Task for calling the "Clear Carton" Function on the Scan Chamber.
   * Calls the API and updates the UI if needed.
   */
  private async _clearCarton(): Promise<void> {
    console.log(`_clearCarton()'`);

    if (this.service) {
      // Call the "Clear Carton" API"
      let state: PvhScanChamberStateInfoDto;
      try {
        state = await this.pvhService.clearCarton(this.service.id);
      } catch (error) {
        this.handleError(error);
        this.cartonId = "";
        this.isProcessingAction = false;
        this.changeDetectorRef.detectChanges();
        return;
      }

      // Update the state if one was retrieved (if not let the "Refresh" Task handle it)
      if (state != null) {
        this.setState(state);
      }

      // Update the UI
      this.auditQuantityMap.clear();
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Called when the "Process Carton" UI Button is clicked.
   * Calls the "Process Carton" Function on the Scan Chamber in the background.
   */
  onProcessCartonClicked(): void {
    console.log(`onProcessCartonClicked()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this.indefiniteProgressDialog?.show("Processing", "Processing Carton Audit Attempt...");
      this._processCarton();
    }
  }

  /**
   * Background Task for calling the "Process Carton" Function on the Scan Chamber.
   * Calls the API then updates the UI if needed.
   */
  private async _processCarton(): Promise<void> {
    console.log(`_processCarton()'`);

    if (this.service) {
      // Call the "Process Carton" API
      let state: PvhScanChamberStateInfoDto;
      try {
        const lineItemOverrides: PvhLineItemOverride[] = [];
        const upcsOverridden = Array.from(new Set([...this.auditQuantityMap.keys(), ...this.lineItemReasonCodeAndCommentMap.keys()]));
        upcsOverridden.forEach(upc => lineItemOverrides.push({ upc: upc, audit_quantity: this.auditQuantityMap.get(upc), reason_codes: this.lineItemReasonCodeAndCommentMap.get(upc) } as PvhLineItemOverride));
        
        state = await this.pvhService.processCarton(this.service.id, lineItemOverrides);
      } catch (error) {
        this.handleError(error);
        this.indefiniteProgressDialog?.hide();
        this.isProcessingAction = false;
        this.changeDetectorRef.detectChanges();
        return;
      }

      // Update the state if one was retrieved (if not let the "Refresh" Task handle it)
      if (state != null) {
        this.setState(state);
      }

      // Update the UI
      this.auditQuantityMap.clear();
      this.cartonId = "";
      this.indefiniteProgressDialog?.hide();
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
    }
  }



  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being in error. (RED)
   */
  isErrorRow(lineItem: PvhScanChamberAttemptLineDto): boolean {
    return this.getAuditQuantity(lineItem) > lineItem?.required_quantity && lineItem?.required_quantity > 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighted as
   * being unknown. (GRAY)
   */
  isUnknownRow(lineItem: PvhScanChamberAttemptLineDto): boolean {
    return lineItem?.required_quantity == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being completed successfully. (GREEN)
   */
  isSuccessRow(lineItem: PvhScanChamberAttemptLineDto): boolean {
    return this.getAuditQuantity(lineItem) == lineItem?.required_quantity;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * partially fulfilled. (YELLOW)
   */
  isPartialRow(lineItem: PvhScanChamberAttemptLineDto): boolean {
    const auditQuantity = this.getAuditQuantity(lineItem);
    return (auditQuantity >= 0 && lineItem?.required_quantity > 0 && lineItem?.required_quantity > auditQuantity);
  }
  
  /**
   * Called when the "Add Reason Code / Comment" button is clicked on the UI.
   */
  onReasonCodeAndCommentClicked(): void {
    this.reasonCodeAndCommentDialog.show();
  }

  /**
   * Called when the User selects a Reason Code and optionally a comment to tie to the attempt
   */
  userEnteredReasonCodeAndComment(value: PvhReasonCodeAndComment) {
    if (value.reasonCode) {
      this.enteredReasonCodeAndComment = value;
    } else {
      this.enteredReasonCodeAndComment = undefined;
    }
    this.reasonCodeAndCommentDialog.hide();
  }

  /**
   * Called by the UI to get the Audit Quantity
   */
  getAuditQuantity(lineItem: PvhScanChamberAttemptLineDto): number {
    const override = this.auditQuantityMap.get(lineItem.upc);
    if (override) {
      return override;
    } else {
      return lineItem.scanned_quantity;
    }
  }

  /**
   * Called by the UI when the given Line Item's Audit Quantity column is clicked.
   * Displays the "Audit Quantity" Dialog allowing the User to override the Audit Quantity
   * and set Reason Codes / Comments.
   */
  onAuditQuantityClicked(lineItem: PvhScanChamberAttemptLineDto): void {
    const auditQuantity = this.getAuditQuantity(lineItem);

    console.log(`onAuditQuantityClicked() - Line Item: ${lineItem.upc}, Audit Quantity: ${auditQuantity}`);

    this.auditQuantityDialog.setLineItem(lineItem);
    this.auditQuantityDialog.setAuditQuantity(auditQuantity);
    this.auditQuantityDialog.setReasonCodesAndComments(this.lineItemReasonCodeAndCommentMap.get(lineItem.upc));

    this.auditQuantityDialog.show();
  }

  onAuditQuantityResult(result: PvhAuditQuantityAndReasonCodes): void {
    console.log(`onAuditQuantityResult() - ${ result? JSON.stringify(result) : "*NULL*" }`);
    if (result) {
      if (result.auditQuantity && result.auditQuantity > -1) {
        this.auditQuantityMap.set(result.lineItem.upc, result.auditQuantity);
        if (result.reasonCodesAndComments) {
          this.lineItemReasonCodeAndCommentMap.set(result.lineItem.upc, result.reasonCodesAndComments);
        }
      } else {
        this.auditQuantityMap.delete(result.lineItem.upc);
        this.lineItemReasonCodeAndCommentMap.delete(result.lineItem.upc);
      }
      this.auditQuantityDialog.hide();
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    if (error?.error != null) {
      //alert(error.error);
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
